import { adminLookupsService } from '../lookups/services';
import { apiDelete, apiGet, apiPost, apiPut, request } from '@/shared/utils/requestBuilder';
import { readAgentResponse } from '../../utils/negotiationAgents/apiResponse';
import type {
  AgentAuxiliaryCandidatePage,
  AgentAuxiliaryCandidateQuery,
  AgentAvailabilityMutation,
  AgentEscritorioPage,
  AgentEscritorioQuery,
  AgentCatalogPage,
  AgentCatalogQuery,
  AgentCatalogItem,
  AgentInternalLine,
  AgentLinesQuery,
  AgentListQuery,
  AgentMutation,
  AgentMutationResult,
  AgentPage,
  AgentResource,
  AgentResourceQuery,
  AgentValidation,
  AgentValidationQuery,
  NegotiationAgent,
} from '../../types/negotiationAgents';
import type {
  AgentRunningMigrationApplyInput,
  AgentRunningMigrationPreview,
  AgentRunningMigrationResult,
} from '../../types/agentRunningMigration';

const baseUrl = '/admin/negotiationAgents';

const PATH_KEYS = ['agentId', 'resource'];
// No catálogo o `kind` é o segmento da rota; na listagem de agentes ele é filtro de query.
const CATALOG_PATH_KEYS = [...PATH_KEYS, 'kind'];
const SHARED_LIBRARY_PATH_KEYS = [...PATH_KEYS, 'sharedLibrary'];
const SHARED_LIBRARY_KINDS = { fields: 'sharedFields', blocks: 'sharedBlocks' } as const;

function queryString(
  query:
    | AgentListQuery
    | AgentResourceQuery
    | AgentCatalogQuery
    | AgentLinesQuery
    | AgentAuxiliaryCandidateQuery,
  pathKeys: readonly string[] = PATH_KEYS,
): string {
  const params = new URLSearchParams();
  Object.entries({ page: 1, limit: 12, ...query }).forEach(([key, value]) => {
    if (!pathKeys.includes(key) && value !== undefined && value !== '')
      params.set(key, String(value));
  });
  return params.toString();
}

export const negotiationAgentsService = {
  offices: async (query: AgentEscritorioQuery): Promise<AgentEscritorioPage> =>
    adminLookupsService.escritorios(query),
  catalogs: async (query: AgentCatalogQuery): Promise<AgentCatalogPage<AgentCatalogItem>> => {
    const page = await readAgentResponse<AgentCatalogPage<AgentCatalogItem>>(
      await apiGet(
        `/admin/flows/catalogs/${encodeURIComponent(query.kind)}?${queryString(query, CATALOG_PATH_KEYS)}`,
      ),
    );
    if (query.kind === 'templates') {
      const { items, total, page: pageNumber, limit, hasMore, nextCursor } = page;
      return {
        items,
        total,
        page: pageNumber,
        limit,
        hasMore,
        ...(nextCursor === undefined ? {} : { nextCursor }),
      };
    }
    if (query.kind !== 'signatureZapsign') return page;
    return {
      ...page,
      items: page.items.map((item) => ({
        ...item,
        token: 'token' in item ? item.token : 'id' in item ? item.id : '',
      })),
    };
  },
  list: async (query: AgentListQuery): Promise<AgentPage<NegotiationAgent>> =>
    readAgentResponse(await apiGet(`${baseUrl}?${queryString(query)}`)),
  detail: async (id: string): Promise<NegotiationAgent> =>
    readAgentResponse(await apiGet(`${baseUrl}/${encodeURIComponent(id)}`)),
  updateAvailability: async ({
    agentId,
    ...availability
  }: AgentAvailabilityMutation): Promise<NegotiationAgent> =>
    readAgentResponse(
      await apiPut(`${baseUrl}/${encodeURIComponent(agentId)}/availability`, availability),
    ),
  auxiliaryCandidates: async (
    query: AgentAuxiliaryCandidateQuery,
  ): Promise<AgentAuxiliaryCandidatePage> =>
    readAgentResponse(
      await apiGet(
        `${baseUrl}/${encodeURIComponent(query.agentId)}/auxiliaryResources/candidates?${queryString(query)}`,
      ),
    ),
  lines: async (query: AgentLinesQuery): Promise<AgentPage<AgentInternalLine>> =>
    readAgentResponse(
      await apiGet(`${baseUrl}/${encodeURIComponent(query.agentId)}/lines?${queryString(query)}`),
    ),
  resources: async (query: AgentResourceQuery): Promise<AgentPage<AgentResource>> => {
    const legacyKinds = {
      flows: 'legacyFlows',
      fields: 'legacyFields',
      functions: 'legacyFunctions',
      blocks: 'legacyBlocks',
    };
    if (!query.agentId && query.resource === 'versions' && query.flowId) {
      const page = await readAgentResponse<AgentPage<AgentResource>>(
        await apiGet(
          `/admin/flows/${encodeURIComponent(query.flowId)}/versions?${queryString(query)}`,
        ),
      );
      return { ...page, hasMore: page.page * page.limit < page.total };
    }
    if (!query.agentId && query.sharedLibrary) {
      if (query.resource !== 'fields' && query.resource !== 'blocks')
        throw new Error('A Biblioteca compartilhada só tem campos e blocos.');
      return readAgentResponse(
        await apiGet(
          `/admin/flows/catalogs/${SHARED_LIBRARY_KINDS[query.resource]}?${queryString(query, SHARED_LIBRARY_PATH_KEYS)}`,
        ),
      );
    }
    if (!query.agentId) {
      if (!query.domain || !(query.resource in legacyKinds))
        throw new Error('Contexto do catálogo legado ausente.');
      const kind = legacyKinds[query.resource as keyof typeof legacyKinds];
      return readAgentResponse(await apiGet(`/admin/flows/catalogs/${kind}?${queryString(query)}`));
    }
    return readAgentResponse<AgentPage<AgentResource>>(
      await apiGet(
        `${baseUrl}/${encodeURIComponent(query.agentId)}/${encodeURIComponent(query.resource)}?${queryString(query)}`,
      ),
    );
  },
  previewRunningMigration: async (agentId: string): Promise<AgentRunningMigrationPreview> =>
    readAgentResponse(
      await apiPost(`${baseUrl}/${encodeURIComponent(agentId)}/runningMigration/preview`, {}),
    ),
  applyRunningMigration: async ({
    agentId,
    planHash,
  }: AgentRunningMigrationApplyInput): Promise<AgentRunningMigrationResult> =>
    readAgentResponse(
      await apiPost(`${baseUrl}/${encodeURIComponent(agentId)}/runningMigration/apply`, {
        planHash,
      }),
    ),
  validate: async (query: AgentValidationQuery): Promise<AgentValidation> =>
    readAgentResponse(
      await apiPost(
        `${baseUrl}/${encodeURIComponent(query.agentId)}/validate?page=${query.page ?? 1}&limit=${query.limit ?? 20}`,
        {},
      ),
    ),
  async mutate(input: AgentMutation): Promise<AgentMutationResult['result']> {
    const agentUrl = input.agentId ? `${baseUrl}/${encodeURIComponent(input.agentId)}` : baseUrl;
    const data = input.data ?? {};
    if (!input.agentId && input.resource === 'flows' && input.id && input.operation === 'publish')
      return readAgentResponse(
        await apiPost(`/admin/flows/${encodeURIComponent(input.id)}/publish`, data),
      );
    if (
      !input.agentId &&
      input.resource === 'flows' &&
      input.id &&
      input.operation === 'restoreVersion'
    ) {
      if (!('versionId' in data) || typeof data.versionId !== 'string')
        throw new Error('Versão ausente.');
      return readAgentResponse(
        await apiPost(
          `/admin/flows/${encodeURIComponent(input.id)}/versions/${encodeURIComponent(data.versionId)}/restore`,
          {},
        ),
      );
    }
    if (input.operation === 'publish')
      return readAgentResponse(await apiPost(`${agentUrl}/publish`, data));
    if (input.operation === 'copyPreview')
      return readAgentResponse(await apiPost(`${agentUrl}/copy/preview`, data));
    if (input.operation === 'copyCommit')
      return readAgentResponse(await apiPost(`${agentUrl}/copy/commit`, data));
    if (input.operation === 'bindAuxiliary')
      return readAgentResponse(await apiPost(`${agentUrl}/auxiliaryResources`, data));
    if (input.operation === 'bindField')
      return readAgentResponse(await apiPost(`${agentUrl}/fields/bind`, data));
    if (!input.resource && input.agentId && input.operation === 'delete') {
      const response = await request({ endpoint: agentUrl, method: 'DELETE', body: data });
      if (!response.ok) await readAgentResponse(response);
      return null;
    }
    if (!input.resource)
      return readAgentResponse(
        await (input.operation === 'create' ? apiPost(baseUrl, data) : apiPut(agentUrl, data)),
      );
    const routes = {
      flows: '/admin/flows',
      functions: '/functions',
      fields: '/admin/custom-fields',
      blocks: '/admin/flow-custom-groups',
      versions: `${agentUrl}/versions`,
    };
    if (input.resource === 'blockedMessages' || input.resource === 'auxiliaryResources')
      throw new Error('Este recurso é somente leitura.');
    const url = `${routes[input.resource]}${input.id ? `/${encodeURIComponent(input.id ?? '')}` : ''}`;
    if (input.operation === 'delete') {
      const response = await apiDelete(url);
      if (!response.ok) await readAgentResponse(response);
      return null;
    }
    return readAgentResponse(
      await (input.operation === 'create'
        ? apiPost(url, { ...data, negotiationAgentId: input.agentId })
        : apiPut(url, { ...data, negotiationAgentId: input.agentId })),
    );
  },
};
